import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./time-range-picker.scss";
import { AccessTimeRounded, Close } from "@mui/icons-material";
import { Box, IconButton, InputAdornment } from "@mui/material";
import TextField from "../text-field/text-field";

interface TimeRangePickerProps {
  hour12?: boolean;
  startTime?: string;
  endTime?: string;
  onTimeRangeChange: (timeRanges: any[]) => void;
}
const TimeRangePicker: React.FC<TimeRangePickerProps> = (props: any) => {
  const [timeRange, setTimeRange] = useState<string[]>([]);
  const [differenceInMinutes, setDifferenceInMinutes] = useState(0);
  const [cnt, setCnt] = useState(0);
  const [isOpenTimePicker, setIsOpenTimePicker] = useState(undefined);
  const { hour12 = false, startTime, endTime, onTimeRangeChange } = props;

  useEffect(() => {
    setTimeRange([]);

    const formatDate = (time: string) => {
      if (!time) return null;

      const [timePart, meridiem] = time.split(" ");
      const [hours, minutes] = timePart.split(":").map(Number);

      let formattedHours = hours;

      if (meridiem) {
        if (meridiem === "PM") {
          if (hours !== 12) {
            formattedHours += 12;
          }
        } else if (meridiem === "AM" && hours === 12) {
          formattedHours -= 12;
        }
      }

      const date = new Date();
      date.setHours(formattedHours, minutes, 0, 0);

      const formattedTime = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: hour12,
      });
      let finalFormattedTime = formattedTime;

      if (date.getHours() === 0) {
        finalFormattedTime = "00" + formattedTime.slice(2); // Replace hours with 00
      } else if (date.getHours() === 12) {
        finalFormattedTime = "12" + formattedTime.slice(2); // Replace hours with 12
      }
      return finalFormattedTime;
    };

    setTimeRange((prevTimeRange: any) => {
      if (startTime && endTime) {
        const formattedStartTime = formatDate(startTime);
        const formattedEndTime = formatDate(endTime);
        return [...prevTimeRange, formattedStartTime, formattedEndTime];
      }
      return prevTimeRange;
    });
  }, [hour12, startTime, endTime]);

  const handleChange = (dates: any) => {
    const formattedTime: any = dates.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: hour12,
    });

    let finalFormattedTime = formattedTime;

    if (dates.getHours() === 0) {
      finalFormattedTime = "00" + formattedTime.slice(2); // Replace hours with 00
    } else if (dates.getHours() === 12) {
      finalFormattedTime = "12" + formattedTime.slice(2); // Replace hours with 12
    }

    if (timeRange.length < 2) {
      setTimeRange([...timeRange, finalFormattedTime]);
    } else if (cnt === 0) {
      setTimeRange((prevTimeRange) => {
        const updatedTimeRange: any = [...prevTimeRange];
        updatedTimeRange[0] = finalFormattedTime;
        return updatedTimeRange;
      });
    } else if (cnt === 1) {
      setTimeRange((prevTimeRange) => {
        const updatedTimeRange: any = [...prevTimeRange];
        updatedTimeRange[1] = finalFormattedTime;
        return updatedTimeRange;
      });
    }
    setCnt(cnt + 1);
  };

  useEffect(() => {
    if (cnt === 2) {
      setCnt(0);
    }

    // Calculate the difference between two time ranges
    if (timeRange.length === 2) {
      const startTimeString = timeRange[0];
      const endTimeString = timeRange[1];

      // Parsing start time
      const startTotalMinutes = parseTimeToMinutes(startTimeString);

      // Parsing end time
      const endTotalMinutes = parseTimeToMinutes(endTimeString);

      // Calculating difference
      let difference = endTotalMinutes - startTotalMinutes;
      if (difference < 0) {
        difference += 24 * 60; // Adding 24 hours if end time is smaller than start time
      }
      setDifferenceInMinutes(difference);
    } else {
      setDifferenceInMinutes(0);
    }
  }, [cnt, timeRange]);

  const parseTimeToMinutes = (timeString: string) => {
    const [hoursStr, minutesStr, meridiem] = timeString.split(/:| /);
    let hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);

    if (meridiem && meridiem.toLowerCase() === "pm" && hours !== 12) {
      hours += 12;
    } else if (meridiem && meridiem.toLowerCase() === "am" && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  };

  useEffect(() => {
    onTimeRangeChange({
      timeRange: timeRange,
      differenceInMinutes: differenceInMinutes,
    });
  }, [differenceInMinutes, onTimeRangeChange, timeRange]);

  const handleClick = () => {
    const isOpen: any = isOpenTimePicker === true ? undefined : true;
    setIsOpenTimePicker(isOpen);
  };

  const handleCloseClick = (event: any) => {
    setTimeRange([]);
    setCnt(0);
    event.stopPropagation();
  };

  const customInput = (
    <TextField
      className="time-input"
      type="text"
      value={
        timeRange.length === 1
          ? `${timeRange[0]} - `
          : timeRange.length === 2
            ? `${timeRange[0]} - ${timeRange[1]}`
            : ""
      }
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            {timeRange.length > 0 && (
              <IconButton onClick={handleCloseClick}>
                <Close />
              </IconButton>
            )}
            <IconButton onClick={handleClick}>
              <AccessTimeRounded />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <>
      <Box className="DateRangePicker">
        <DatePicker
          className="DatePickerWrapper"
          onChange={(date: any) => {
            handleChange(date), handleClick();
          }}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          selectsRange
          customInput={customInput}
          open={isOpenTimePicker}
          value={
            timeRange && timeRange.length === 1
              ? `${timeRange[0]}-`
              : timeRange && timeRange.length === 2
                ? `${timeRange[0]}-${timeRange[1]}`
                : undefined
          }
        />
        <Box
          className="box-icon"
          style={
            timeRange.length > 0 ? { right: "2.5rem" } : { right: "1.563rem" }
          }
        >
          {timeRange.length > 0 && <Close onClick={handleCloseClick} />}
        </Box>
      </Box>
    </>
  );
};

export default TimeRangePicker;
